import clsx from 'clsx';
import { useMemo } from 'react';

import {
  calculateFinalScore,
  calculateGameResults,
} from '@/modules/players/v2/tabs/rating-history/utils';

import { ScoreCardProps } from '../../types';

export const ScoreCard = ({ teamOneScores, teamTwoScores, winner }: ScoreCardProps) => {
  const isTeamOneWinner = winner === '1';
  const gameResults = calculateGameResults(teamOneScores, teamTwoScores);
  const finalScore = calculateFinalScore(gameResults);

  const gameResultsWithKeys = useMemo(() => {
    return gameResults.map((game) => ({ ...game, _key: crypto.randomUUID() }));
  }, [gameResults]);

  return (
    <div>
      {/* Mobile view (smaller than md screens) */}
      <div className='flex justify-center md:hidden'>
        <div className='relative flex w-52 flex-col items-center'>
          <span
            className={`w-full text-center text-2xl font-semibold ${isTeamOneWinner ? 'text-green-light-700' : 'text-gray-700'}`}
          >
            {finalScore.numberOfGamesTeamOneWon}
          </span>
          {isTeamOneWinner ? (
            <span className='absolute right-16 top-1 font-bold text-green-light-700'>{'<'}</span>
          ) : null}
          <div className={`flex gap-3`}>
            {gameResultsWithKeys.map((game) => {
              const isWinnerInSet = game.teamOneScore > game.teamTwoScore;

              return (
                <div
                  key={game._key}
                  className='flex flex-col items-center text-center text-base font-medium'
                >
                  <span className={`${!isWinnerInSet ? 'text-gray-700' : 'text-green-light-700'}`}>
                    {game.teamOneScore}
                  </span>
                  <span className='my-1 h-px w-full bg-gray-300' />
                  <span className={`${isWinnerInSet ? 'text-gray-700' : 'text-green-light-700'}`}>
                    {game.teamTwoScore}
                  </span>
                </div>
              );
            })}
          </div>

          <span
            className={clsx('w-full text-center text-2xl font-semibold', {
              'text-green-light-700': !isTeamOneWinner,
              'text-gray-700': isTeamOneWinner,
            })}
          >
            {finalScore.numberOfGamesTeamTwoWon}
          </span>
          {!isTeamOneWinner && (
            <span className='absolute bottom-1 right-16 font-bold text-green-light-700'>{'<'}</span>
          )}
        </div>
      </div>
      {/* Desktop view (larger than md screens) */}
      <div className='hidden md:block'>
        <div className='flex flex-col items-center justify-center gap-5'>
          <div className='relative flex items-center justify-center'>
            <span
              className={clsx('w-[52px] text-center text-2xl font-semibold', {
                'text-green-light-700': isTeamOneWinner,
                'text-gray-700': !isTeamOneWinner,
              })}
            >
              {finalScore.numberOfGamesTeamOneWon}
            </span>
            <span
              className={clsx('w-[52px] text-center text-2xl font-semibold', {
                'text-green-light-700': !isTeamOneWinner,
                'text-gray-700': isTeamOneWinner,
              })}
            >
              {finalScore.numberOfGamesTeamTwoWon}
            </span>
            {isTeamOneWinner ? (
              <span className='absolute -left-3 top-1/2 -translate-y-1/2 font-bold text-green-light-700'>
                {'>'}
              </span>
            ) : (
              <span className='absolute -right-3 top-1/2 -translate-y-1/2 font-bold text-green-light-700'>
                {'<'}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-2'>
            {gameResultsWithKeys.map((game) => {
              const isWinnerInSet = game.teamOneScore > game.teamTwoScore;

              return (
                <div key={game._key} className='flex items-center justify-between text-center'>
                  <span
                    className={clsx('w-14 text-base font-medium', {
                      'text-green-light-700': isWinnerInSet,
                      'text-gray-700': !isWinnerInSet,
                    })}
                  >
                    {game.teamOneScore}
                  </span>
                  <span className='h-4 w-px bg-gray-300' />
                  <span
                    className={clsx('w-14 text-base font-medium', {
                      'text-green-light-700': !isWinnerInSet,
                      'text-gray-700': isWinnerInSet,
                    })}
                  >
                    {game.teamTwoScore}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
